import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { mapboxSuggest, mapboxRetrieve, type MapboxSuggestion } from '@/lib/mapbox';
import { searchIvrPois, type IvrPoi } from '@/lib/ivrPois';
import {
  Loader2,
  Plus,
  Trash2,
  Home,
  MapPin,
  MapPinned,
  Save,
  X,
  Pencil,
  Search,
  GripVertical,
  Eye,
  EyeOff,
  AlertTriangle,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';


export interface BookableAddress {
  id?: string;
  account_id: string;
  address_type: 'home' | 'destination';
  label: string;
  address_line_1: string;
  address_line_2: string;
  suburb: string;
  city: string;
  postcode: string;
  full_address: string;
  notes: string;
  is_active: boolean;
  sort_order: number;
  latitude: number | null;
  longitude: number | null;
}



interface CRMBookableAddressesProps {
  accountId: string;
  readOnly?: boolean;
  openApproval?: boolean;
}


const EMPTY_ADDRESS: Omit<BookableAddress, 'account_id'> = {
  address_type: 'destination',
  label: '',
  address_line_1: '',
  address_line_2: '',
  suburb: '',
  city: '',
  postcode: '',
  full_address: '',
  notes: '',
  is_active: true,
  sort_order: 0,
  latitude: null,
  longitude: null,
};

const CRMBookableAddresses: React.FC<CRMBookableAddressesProps> = ({
  accountId,
  readOnly = false,
  openApproval = false,
}) => {

  const [addresses, setAddresses] = useState<BookableAddress[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Editing state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Omit<BookableAddress, 'account_id'>>(EMPTY_ADDRESS);
  const [isAdding, setIsAdding] = useState(false);
  const [addType, setAddType] = useState<'home' | 'destination'>('destination');

  // Drag and drop state
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [savingOrder, setSavingOrder] = useState(false);

  // Toggle active loading state
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // Delete confirmation state
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Mapbox autocomplete state
  const [suggestions, setSuggestions] = useState<MapboxSuggestion[]>([]);
  const [poiSuggestions, setPoiSuggestions] = useState<IvrPoi[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchingAddress, setSearchingAddress] = useState(false);
  const [retrievingAddress, setRetrievingAddress] = useState(false);
  const sessionTokenRef = useRef<string>(crypto.randomUUID());
  const suggestTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const addressInputRef = useRef<HTMLInputElement>(null);

  const loadAddresses = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('crm_account_addresses')
        .select('*')
        .eq('account_id', accountId)
        .order('address_type', { ascending: true })
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true });
      if (error) throw error;
      setAddresses((data || []) as BookableAddress[]);
    } catch (err: any) {
      console.error('Error loading addresses:', err);
      toast({ title: 'Error', description: 'Failed to load addresses', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    loadAddresses();
  }, [loadAddresses]);

  // Close suggestions on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target as Node) &&
        addressInputRef.current &&
        !addressInputRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const homeAddress = addresses.find((a) => a.address_type === 'home');
  const destinations = addresses.filter((a) => a.address_type === 'destination');

  const buildFullAddress = (form: Omit<BookableAddress, 'account_id'>) => {
    return [form.address_line_1, form.address_line_2, form.suburb, form.city, form.postcode]
      .filter(Boolean)
      .join(', ');
  };

  const handleStartAdd = (type: 'home' | 'destination') => {
    setAddType(type);
    setEditForm({
      ...EMPTY_ADDRESS,
      address_type: type,
      label: type === 'home' ? 'Home' : '',
      sort_order: destinations.length,
    });
    setIsAdding(true);
    setEditingId(null);
    setSuggestions([]);
    setShowSuggestions(false);
    sessionTokenRef.current = crypto.randomUUID();
  };

  const handleStartEdit = (address: BookableAddress) => {
    setEditingId(address.id || null);
    setEditForm({
      address_type: address.address_type,
      label: address.label || '',
      address_line_1: address.address_line_1 || '',
      address_line_2: address.address_line_2 || '',
      suburb: address.suburb || '',
      city: address.city || '',
      postcode: address.postcode || '',
      full_address: address.full_address || '',
      notes: address.notes || '',
      is_active: address.is_active,
      sort_order: address.sort_order || 0,
      latitude: address.latitude ?? null,
      longitude: address.longitude ?? null,
    });
    setIsAdding(false);
    setSuggestions([]);
    setShowSuggestions(false);
    sessionTokenRef.current = crypto.randomUUID();
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setIsAdding(false);
    setEditForm(EMPTY_ADDRESS);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleSave = async () => {
    if (!editForm.address_line_1.trim()) {
      toast({ title: 'Required', description: 'Address Line 1 is required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const fullAddr = buildFullAddress(editForm);
      const payload = {
        account_id: accountId,
        address_type: editForm.address_type,
        label: editForm.label.trim() || null,
        address_line_1: editForm.address_line_1.trim() || null,
        address_line_2: editForm.address_line_2.trim() || null,
        suburb: editForm.suburb.trim() || null,
        city: editForm.city.trim() || null,
        postcode: editForm.postcode.trim() || null,
        full_address: fullAddr || null,
        notes: editForm.notes.trim() || null,
        is_active: editForm.is_active,
        sort_order: editForm.sort_order,
        latitude: editForm.latitude,
        longitude: editForm.longitude,
        updated_at: new Date().toISOString(),
      };

      if (isAdding) {
        const { error } = await supabase
          .from('crm_account_addresses')
          .insert({ ...payload, created_at: new Date().toISOString() });
        if (error) throw error;
        toast({ title: 'Address Added', description: `${editForm.address_type === 'home' ? 'Home' : 'Destination'} address saved.` });
      } else if (editingId) {
        const { error } = await supabase
          .from('crm_account_addresses')
          .update(payload)
          .eq('id', editingId);
        if (error) throw error;
        toast({ title: 'Address Updated', description: 'Address has been updated.' });
      }

      handleCancelEdit();
      loadAddresses();
    } catch (err: any) {
      console.error('Error saving address:', err);
      toast({ title: 'Error', description: err.message || 'Failed to save address', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('crm_account_addresses')
        .delete()
        .eq('id', id);
      if (error) throw error;
      toast({ title: 'Address Removed', description: 'The address has been permanently deleted.' });
      setConfirmDeleteId(null);
      loadAddresses();
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to delete', variant: 'destructive' });
    }
  };

  // Toggle active/inactive
  const handleToggleActive = async (address: BookableAddress) => {
    if (!address.id) return;
    setTogglingId(address.id);
    try {
      const newActive = !address.is_active;
      const { error } = await supabase
        .from('crm_account_addresses')
        .update({
          is_active: newActive,
          updated_at: new Date().toISOString(),
        })
        .eq('id', address.id);
      if (error) throw error;
      toast({
        title: newActive ? 'Address Activated' : 'Address Deactivated',
        description: `${address.label || address.address_line_1 || 'Address'} is now ${newActive ? 'active' : 'inactive'}.`,
      });
      // Update local state immediately for responsiveness
      setAddresses((prev) =>
        prev.map((a) => (a.id === address.id ? { ...a, is_active: newActive } : a))
      );
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to update status', variant: 'destructive' });
    } finally {
      setTogglingId(null);
    }
  };

  // ─── Drag and Drop Handlers ───────────────────────────────────────────

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    // Set a transparent drag image for cleaner look
    const el = e.currentTarget as HTMLElement;
    if (el) {
      e.dataTransfer.setDragImage(el, el.offsetWidth / 2, el.offsetHeight / 2);
    }
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    // Reorder destinations array
    const newDestinations = [...destinations];
    const [draggedItem] = newDestinations.splice(draggedIndex, 1);
    newDestinations.splice(dropIndex, 0, draggedItem);

    // Update sort_order for each destination
    const updatedDestinations = newDestinations.map((dest, idx) => ({
      ...dest,
      sort_order: idx,
    }));

    // Update local state immediately
    const newAddresses = addresses.map((a) => {
      if (a.address_type === 'home') return a;
      const updated = updatedDestinations.find((d) => d.id === a.id);
      return updated || a;
    });
    // Sort: home first, then by sort_order
    newAddresses.sort((a, b) => {
      if (a.address_type === 'home' && b.address_type !== 'home') return -1;
      if (a.address_type !== 'home' && b.address_type === 'home') return 1;
      return (a.sort_order || 0) - (b.sort_order || 0);
    });
    setAddresses(newAddresses);

    setDraggedIndex(null);
    setDragOverIndex(null);

    // Persist to database
    await persistSortOrder(updatedDestinations);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const persistSortOrder = async (orderedDestinations: BookableAddress[]) => {
    setSavingOrder(true);
    try {
      // Update each destination's sort_order in the database
      const updates = orderedDestinations.map((dest, idx) =>
        supabase
          .from('crm_account_addresses')
          .update({ sort_order: idx, updated_at: new Date().toISOString() })
          .eq('id', dest.id!)
      );
      const results = await Promise.all(updates);
      const hasError = results.some((r) => r.error);
      if (hasError) {
        throw new Error('Some sort order updates failed');
      }
      toast({ title: 'Order Saved', description: 'Address order has been updated.' });
    } catch (err: any) {
      console.error('Error saving sort order:', err);
      toast({ title: 'Error', description: 'Failed to save address order. Reloading...', variant: 'destructive' });
      loadAddresses(); // Reload to get correct order
    } finally {
      setSavingOrder(false);
    }
  };

  // ─── Touch Drag Support ───────────────────────────────────────────────
  const touchStartY = useRef<number>(0);
  const touchDragIndex = useRef<number | null>(null);
  const touchCurrentIndex = useRef<number | null>(null);
  const destListRef = useRef<HTMLDivElement>(null);

  const handleTouchStart = (e: React.TouchEvent, index: number) => {
    touchStartY.current = e.touches[0].clientY;
    touchDragIndex.current = index;
    setDraggedIndex(index);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchDragIndex.current === null || !destListRef.current) return;
    const touch = e.touches[0];
    const children = Array.from(destListRef.current.children) as HTMLElement[];
    
    for (let i = 0; i < children.length; i++) {
      const rect = children[i].getBoundingClientRect();
      if (touch.clientY >= rect.top && touch.clientY <= rect.bottom) {
        if (i !== touchDragIndex.current) {
          setDragOverIndex(i);
          touchCurrentIndex.current = i;
        }
        break;
      }
    }
  };

  const handleTouchEnd = async () => {
    if (touchDragIndex.current !== null && touchCurrentIndex.current !== null && touchDragIndex.current !== touchCurrentIndex.current) {
      const newDestinations = [...destinations];
      const [draggedItem] = newDestinations.splice(touchDragIndex.current, 1);
      newDestinations.splice(touchCurrentIndex.current, 0, draggedItem);

      const updatedDestinations = newDestinations.map((dest, idx) => ({
        ...dest,
        sort_order: idx,
      }));

      const newAddresses = addresses.map((a) => {
        if (a.address_type === 'home') return a;
        const updated = updatedDestinations.find((d) => d.id === a.id);
        return updated || a;
      });
      newAddresses.sort((a, b) => {
        if (a.address_type === 'home' && b.address_type !== 'home') return -1;
        if (a.address_type !== 'home' && b.address_type === 'home') return 1;
        return (a.sort_order || 0) - (b.sort_order || 0);
      });
      setAddresses(newAddresses);

      await persistSortOrder(updatedDestinations);
    }

    touchDragIndex.current = null;
    touchCurrentIndex.current = null;
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Helper to update a single field in editForm
  const updateEditField = (field: string, value: string) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  // Mapbox suggest handler - called when Address Line 1 changes
  const handleAddressSearch = (query: string) => {
    updateEditField('address_line_1', query);
    // Retyping invalidates any coordinates captured for a previously-selected
    // suggestion -- they only become valid again once a new one is picked.
    setEditForm((prev) => ({ ...prev, latitude: null, longitude: null }));

    // Clear previous timer
    if (suggestTimerRef.current) {
      clearTimeout(suggestTimerRef.current);
    }

    if (query.length < 3) {
      setSuggestions([]);
      setPoiSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Debounce the search by 350ms
    suggestTimerRef.current = setTimeout(async () => {
      setSearchingAddress(true);
      try {
        // Run independently -- a Mapbox hiccup shouldn't hide POI matches
        // (our own hand-pinned landmarks) and vice versa.
        const [mapboxResult, poiResult] = await Promise.allSettled([
          mapboxSuggest(query, sessionTokenRef.current),
          searchIvrPois(query),
        ]);
        const results = mapboxResult.status === 'fulfilled' ? mapboxResult.value : [];
        const pois = poiResult.status === 'fulfilled' ? poiResult.value : [];
        if (mapboxResult.status === 'rejected') console.error('Mapbox suggest error:', mapboxResult.reason);
        if (poiResult.status === 'rejected') console.error('POI search error:', poiResult.reason);
        setSuggestions(results);
        setPoiSuggestions(pois);
        setShowSuggestions(results.length > 0 || pois.length > 0);
      } finally {
        setSearchingAddress(false);
      }
    }, 350);
  };

  // POI selection -- we already have coordinates on file (from the IVR's own
  // hand-pinned landmark list), so this skips the Mapbox retrieve step
  // entirely. There's no suburb/city/postcode on a POI row, so those fields
  // are cleared rather than left stale from whatever was typed before.
  const handleSelectPoi = (poi: IvrPoi, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setShowSuggestions(false);
    setSuggestions([]);
    setPoiSuggestions([]);
    setEditForm((prev) => ({
      ...prev,
      address_line_1: poi.name,
      address_line_2: '',
      suburb: '',
      city: '',
      postcode: '',
      full_address: poi.name,
      latitude: poi.lat,
      longitude: poi.lon,
    }));
    sessionTokenRef.current = crypto.randomUUID();
  };

  // Mapbox retrieve handler - called when a suggestion is selected
  const handleSelectSuggestion = async (suggestion: MapboxSuggestion, e?: React.MouseEvent) => {
    // Prevent default to stop focus loss and stop propagation to prevent Dialog from intercepting
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    setShowSuggestions(false);
    setSuggestions([]);
    setRetrievingAddress(true);

    try {
      const addr = await mapboxRetrieve(suggestion.mapbox_id, sessionTokenRef.current);

      setEditForm((prev) => ({
        ...prev,
        address_line_1: addr.address_line_1 || suggestion.name || prev.address_line_1,
        address_line_2: addr.address_line_2 || prev.address_line_2,
        suburb: addr.suburb || prev.suburb,
        city: addr.city || prev.city,
        postcode: addr.postcode || prev.postcode,
        full_address: addr.full_address || suggestion.full_address || prev.full_address,
        latitude: addr.latitude,
        longitude: addr.longitude,
      }));

      // Generate a new session token after a successful retrieve (Mapbox billing best practice)
      sessionTokenRef.current = crypto.randomUUID();
    } catch (err: any) {
      console.error('Mapbox retrieve error:', err);
      // Fallback: parse what we can from the suggestion itself
      const fallbackLine1 = suggestion.name || suggestion.address || '';
      const fallbackFull = suggestion.full_address || suggestion.place_formatted || '';

      // Try to extract suburb/city/postcode from place_formatted or context
      let fallbackSuburb = '';
      let fallbackCity = '';
      let fallbackPostcode = '';

      // Try to parse from context if available
      if (suggestion.context) {
        const ctx = suggestion.context;
        fallbackSuburb = ctx.locality?.name || ctx.neighborhood?.name || ctx.place?.name || '';
        fallbackCity = ctx.region?.name || ctx.district?.name || ctx.place?.name || '';
        fallbackPostcode = ctx.postcode?.name || '';
      }

      // If no context, try parsing from place_formatted (e.g. "Ashhurst 4810, New Zealand")
      if (!fallbackSuburb && !fallbackCity && suggestion.place_formatted) {
        const parts = suggestion.place_formatted.split(',').map((p: string) => p.trim());
        if (parts.length >= 1) {
          // First part might be "Suburb Postcode" or just "Suburb"
          const firstPart = parts[0];
          const postcodeMatch = firstPart.match(/(\d{4,5})$/);
          if (postcodeMatch) {
            fallbackPostcode = postcodeMatch[1];
            fallbackSuburb = firstPart.replace(postcodeMatch[1], '').trim();
          } else {
            fallbackSuburb = firstPart;
          }
        }
        if (parts.length >= 2 && parts[parts.length - 1] !== 'New Zealand') {
          fallbackCity = parts[parts.length - 1];
        }
      }

      setEditForm((prev) => ({
        ...prev,
        address_line_1: fallbackLine1 || prev.address_line_1,
        suburb: fallbackSuburb || prev.suburb,
        city: fallbackCity || prev.city,
        postcode: fallbackPostcode || prev.postcode,
        full_address: fallbackFull || prev.full_address,
      }));
    } finally {
      setRetrievingAddress(false);
    }
  };



  // Input class for consistency
  const inputClass =
    'flex h-10 w-full rounded-md border border-yellow-600/40 bg-black/40 px-3 py-2 text-sm text-foreground ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500/50 focus-visible:ring-offset-2';

  // Inline address form JSX - NOT a component, just a function returning JSX
  const renderAddressForm = () => (
    <div className="border border-yellow-600/40 rounded-lg p-4 bg-yellow-900/10 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-yellow-400">
          {isAdding
            ? editForm.address_type === 'home'
              ? 'Add Home Address'
              : 'Add Approved Destination'
            : 'Edit Address'}
        </p>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={handleCancelEdit}
          className="text-muted-foreground h-7"
        >
          <X className="w-3.5 h-3.5" />
        </Button>
      </div>

      {editForm.address_type === 'destination' && (
        <div className="space-y-1.5">
          <Label className="text-foreground text-sm">Label</Label>
          <input
            type="text"
            value={editForm.label}
            onChange={(e) => updateEditField('label', e.target.value)}
            className={inputClass}
            placeholder="e.g. Hospital, Work, Day Centre..."
          />
        </div>
      )}

      {/* Address Line 1 with Mapbox Autofill */}
      <div className="space-y-1.5">
        <Label className="text-foreground text-sm flex items-center gap-2">
          Address Line 1 <span className="text-red-400">*</span>
          {(searchingAddress || retrievingAddress) && (
            <Loader2 className="w-3 h-3 animate-spin text-yellow-500" />
          )}
        </Label>
        <div className="relative">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <input
              ref={addressInputRef}
              type="text"
              value={editForm.address_line_1}
              onChange={(e) => handleAddressSearch(e.target.value)}
              onFocus={() => {
                if (suggestions.length > 0) setShowSuggestions(true);
              }}
              className={`${inputClass} pl-9`}
              placeholder="Start typing an address..."
              autoComplete="off"
            />
          </div>

          {/* Mapbox + POI Suggestions Dropdown */}
          {showSuggestions && (suggestions.length > 0 || poiSuggestions.length > 0) && (
            <div
              ref={suggestionsRef}
              className="absolute z-[9999] w-full mt-1 bg-card border border-yellow-600/40 rounded-lg shadow-2xl max-h-[260px] overflow-y-auto"
              onMouseDown={(e) => {
                // CRITICAL: Prevent default to stop the input from losing focus,
                // and stop propagation to prevent Dialog/Modal from intercepting the event
                e.preventDefault();
                e.stopPropagation();
              }}
              onPointerDown={(e) => {
                // Also handle pointer events for Radix UI Dialog compatibility
                e.stopPropagation();
              }}
            >
              {poiSuggestions.length > 0 && (
                <>
                  <div className="px-3 py-1.5 border-b border-border/50 bg-blue-950/20">
                    <p className="text-[10px] text-blue-300 uppercase tracking-wider font-medium">
                      Local Landmarks (POI)
                    </p>
                  </div>
                  {poiSuggestions.map((poi) => (
                    <div
                      key={poi.id}
                      role="button"
                      tabIndex={0}
                      className="w-full text-left px-3 py-2.5 text-sm hover:bg-blue-900/20 transition-colors flex items-start gap-2.5 border-b border-border/20 cursor-pointer select-none"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSelectPoi(poi, e as unknown as React.MouseEvent);
                      }}
                      onPointerDown={(e) => {
                        e.stopPropagation();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSelectPoi(poi);
                        }
                      }}
                    >
                      <MapPinned className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <p className="text-foreground font-medium truncate">{poi.name}</p>
                        {poi.notes && <p className="text-xs text-muted-foreground truncate">{poi.notes}</p>}
                      </div>
                      <Badge variant="secondary" className="text-[9px] flex-shrink-0 mt-0.5">
                        POI
                      </Badge>
                    </div>
                  ))}
                </>
              )}
              {suggestions.length > 0 && (
                <div className="px-3 py-1.5 border-b border-border/50">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">
                    Mapbox Address Suggestions
                  </p>
                </div>
              )}
              {suggestions.map((s, idx) => (
                <div
                  key={s.mapbox_id || idx}
                  role="button"
                  tabIndex={0}
                  className="w-full text-left px-3 py-2.5 text-sm hover:bg-yellow-900/20 transition-colors flex items-start gap-2.5 border-b border-border/20 last:border-b-0 cursor-pointer select-none"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSelectSuggestion(s, e as unknown as React.MouseEvent);
                  }}
                  onPointerDown={(e) => {
                    e.stopPropagation();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSelectSuggestion(s);
                    }
                  }}
                >
                  <MapPin className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="text-foreground font-medium truncate">{s.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{s.place_formatted}</p>
                  </div>
                  {s.feature_type === 'address' && (
                    <Badge variant="secondary" className="text-[9px] flex-shrink-0 mt-0.5">
                      Address
                    </Badge>
                  )}
                </div>
              ))}
              {suggestions.length > 0 && (
                <div className="px-3 py-1.5 border-t border-border/50 bg-secondary/20">
                  <p className="text-[9px] text-muted-foreground text-right">
                    Powered by Mapbox
                  </p>
                </div>
              )}
            </div>
          )}

        </div>
        <p className="text-[11px] text-muted-foreground">
          Type to search — select a suggestion to auto-fill address fields
        </p>
      </div>

      {/* Retrieving indicator */}
      {retrievingAddress && (
        <div className="flex items-center gap-2 py-2 px-3 rounded-md border border-yellow-600/30 bg-yellow-900/10">
          <Loader2 className="w-3.5 h-3.5 animate-spin text-yellow-500" />
          <p className="text-xs text-yellow-400">Fetching address details from Mapbox...</p>
        </div>
      )}

      {/* Geocoded coordinates -- read-only, captured from the Mapbox suggestion
          picked above. Used by the IVR to geo-match a caller's requested
          address against this one; not editable by hand. */}
      {!retrievingAddress && (
        <div
          className={`flex items-center gap-2 py-2 px-3 rounded-md border text-xs ${
            editForm.latitude != null
              ? 'border-green-700/40 bg-green-900/10 text-green-400'
              : 'border-yellow-700/40 bg-yellow-900/10 text-yellow-500/80'
          }`}
        >
          <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
          {editForm.latitude != null ? (
            <span>
              Geocoded: {editForm.latitude.toFixed(5)}, {editForm.longitude!.toFixed(5)}
            </span>
          ) : (
            <span>Not geocoded yet — pick a suggestion above to capture coordinates for the IVR</span>
          )}
        </div>
      )}

      <div className="space-y-1.5">
        <Label className="text-foreground text-sm">Address Line 2</Label>
        <input
          type="text"
          value={editForm.address_line_2}
          onChange={(e) => updateEditField('address_line_2', e.target.value)}
          className={inputClass}
          placeholder="Unit, suite, floor, etc."
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-foreground text-sm">Suburb</Label>
          <input
            type="text"
            value={editForm.suburb}
            onChange={(e) => updateEditField('suburb', e.target.value)}
            className={inputClass}
            placeholder="Suburb"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-foreground text-sm">City</Label>
          <input
            type="text"
            value={editForm.city}
            onChange={(e) => updateEditField('city', e.target.value)}
            className={inputClass}
            placeholder="City"
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-foreground text-sm">Postcode</Label>
        <input
          type="text"
          value={editForm.postcode}
          onChange={(e) => updateEditField('postcode', e.target.value)}
          className={inputClass}
          placeholder="Postcode"
        />
      </div>

      <div className="space-y-1.5">
        <Label className="text-foreground text-sm">Notes</Label>
        <textarea
          value={editForm.notes}
          onChange={(e) => updateEditField('notes', e.target.value)}
          className="flex min-h-[50px] w-full rounded-md border border-yellow-600/40 bg-black/40 px-3 py-2 text-sm text-foreground ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500/50 focus-visible:ring-offset-2 resize-y"
          placeholder="Any notes about this address..."
        />
      </div>

      {/* Active/Inactive toggle in edit form */}
      {!isAdding && (
        <div className="flex items-center gap-3 pt-1">
          <Label className="text-foreground text-sm">Status</Label>
          <button
            type="button"
            className={`flex items-center gap-2 px-3 py-1.5 rounded-md border text-xs font-medium transition-colors ${
              editForm.is_active
                ? 'border-green-600/50 bg-green-900/20 text-green-400 hover:bg-green-900/30'
                : 'border-red-600/50 bg-red-900/20 text-red-400 hover:bg-red-900/30'
            }`}
            onClick={() => setEditForm((prev) => ({ ...prev, is_active: !prev.is_active }))}
          >
            {editForm.is_active ? (
              <>
                <ToggleRight className="w-4 h-4" />
                Active
              </>
            ) : (
              <>
                <ToggleLeft className="w-4 h-4" />
                Inactive
              </>
            )}
          </button>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={handleCancelEdit}
          className="border-border text-muted-foreground"
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={handleSave}
          disabled={saving}
          className="golden-button"
        >
          {saving ? (
            <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
          ) : (
            <Save className="w-3.5 h-3.5 mr-1" />
          )}
          Save Address
        </Button>
      </div>
    </div>
  );

  // Inline address card JSX for HOME address (no drag handle)
  const renderHomeCard = (address: BookableAddress) => {
    const isEditingThis = editingId === address.id;
    if (isEditingThis) return renderAddressForm();

    const fullAddr = address.full_address || [
      address.address_line_1,
      address.address_line_2,
      address.suburb,
      address.city,
      address.postcode,
    ].filter(Boolean).join(', ');

    return (
      <div className={`flex items-start gap-3 rounded-lg border p-3 transition-colors border-blue-700/40 bg-blue-900/10 ${!address.is_active ? 'opacity-50' : ''}`}>
        <div className="flex-shrink-0 mt-0.5">
          <Home className="w-4 h-4 text-blue-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-sm font-medium text-blue-400">Home Address</span>
            {!address.is_active && (
              <Badge className="bg-red-900/40 text-red-400 border-red-700/50 text-[10px]">Inactive</Badge>
            )}
            {address.latitude == null && fullAddr && (
              <Badge
                variant="outline"
                className="text-[10px] border-yellow-700/50 text-yellow-500/80"
                title="No coordinates on file -- re-save via the address search to enable IVR geo-matching"
              >
                Not geocoded
              </Badge>
            )}
          </div>
          <p className="text-sm text-foreground">{fullAddr || 'No address details'}</p>
          {address.notes && (
            <p className="text-xs text-muted-foreground mt-1">{address.notes}</p>
          )}
        </div>
        {!readOnly && (
          <div className="flex items-center gap-0.5 flex-shrink-0">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className={`h-7 w-7 p-0 transition-colors ${
                address.is_active
                  ? 'text-green-400 hover:text-green-300 hover:bg-green-900/20'
                  : 'text-red-400 hover:text-red-300 hover:bg-red-900/20'
              }`}
              onClick={() => handleToggleActive(address)}
              disabled={togglingId === address.id}
              title={address.is_active ? 'Deactivate address' : 'Activate address'}
            >
              {togglingId === address.id ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : address.is_active ? (
                <Eye className="w-3.5 h-3.5" />
              ) : (
                <EyeOff className="w-3.5 h-3.5" />
              )}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
              onClick={() => handleStartEdit(address)}
              title="Edit address"
            >
              <Pencil className="w-3 h-3" />
            </Button>
            {confirmDeleteId === address.id ? (
              <div className="flex items-center gap-1 ml-1">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 text-[10px]"
                  onClick={() => handleDelete(address.id!)}
                >
                  Confirm
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                  onClick={() => setConfirmDeleteId(null)}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0 text-muted-foreground hover:text-red-400"
                onClick={() => setConfirmDeleteId(address.id!)}
                title="Delete address"
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            )}
          </div>
        )}
      </div>
    );
  };

  // Inline address card JSX for DESTINATION addresses (with drag handle)
  const renderDestinationCard = (address: BookableAddress, index: number) => {
    const isEditingThis = editingId === address.id;
    if (isEditingThis) return renderAddressForm();

    const isDragging = draggedIndex === index;
    const isDragOver = dragOverIndex === index;

    const fullAddr = address.full_address || [
      address.address_line_1,
      address.address_line_2,
      address.suburb,
      address.city,
      address.postcode,
    ].filter(Boolean).join(', ');

    return (
      <div
        draggable={!readOnly && !editingId && !isAdding}
        onDragStart={(e) => handleDragStart(e, index)}
        onDragOver={(e) => handleDragOver(e, index)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, index)}
        onDragEnd={handleDragEnd}
        onTouchStart={(e) => !readOnly && handleTouchStart(e, index)}
        onTouchMove={(e) => !readOnly && handleTouchMove(e)}
        onTouchEnd={() => !readOnly && handleTouchEnd()}
        className={`flex items-start gap-2 rounded-lg border p-3 transition-all select-none ${
          isDragging
            ? 'opacity-40 scale-[0.98] border-yellow-500/60 bg-yellow-900/20'
            : isDragOver
            ? 'border-yellow-400/80 bg-yellow-900/15 shadow-[0_0_12px_rgba(234,179,8,0.15)]'
            : 'border-border bg-secondary/30'
        } ${!address.is_active ? 'opacity-50' : ''} ${
          !readOnly && !editingId && !isAdding ? 'cursor-grab active:cursor-grabbing' : ''
        }`}
      >
        {/* Drag Handle */}
        {!readOnly && (
          <div className="flex-shrink-0 mt-1 text-muted-foreground/50 hover:text-muted-foreground transition-colors">
            <GripVertical className="w-4 h-4" />
          </div>
        )}

        <div className="flex-shrink-0 mt-0.5">
          <MapPin className={`w-4 h-4 ${address.is_active ? 'text-muted-foreground' : 'text-red-400/50'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className={`text-sm font-medium ${address.is_active ? 'text-foreground' : 'text-foreground/60'}`}>
              {address.label || 'Destination'}
            </span>
            {!address.is_active && (
              <Badge className="bg-red-900/40 text-red-400 border-red-700/50 text-[10px]">Inactive</Badge>
            )}
            {address.latitude == null && fullAddr && (
              <Badge
                variant="outline"
                className="text-[10px] border-yellow-700/50 text-yellow-500/80"
                title="No coordinates on file -- re-save via the address search to enable IVR geo-matching"
              >
                Not geocoded
              </Badge>
            )}
          </div>
          <p className={`text-sm ${address.is_active ? 'text-foreground' : 'text-foreground/50'}`}>
            {fullAddr || 'No address details'}
          </p>
          {address.notes && (
            <p className="text-xs text-muted-foreground mt-1">{address.notes}</p>
          )}
        </div>
        {!readOnly && (
          <div className="flex items-center gap-0.5 flex-shrink-0">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className={`h-7 w-7 p-0 transition-colors ${
                address.is_active
                  ? 'text-green-400 hover:text-green-300 hover:bg-green-900/20'
                  : 'text-red-400 hover:text-red-300 hover:bg-red-900/20'
              }`}
              onClick={(e) => {
                e.stopPropagation();
                handleToggleActive(address);
              }}
              disabled={togglingId === address.id}
              title={address.is_active ? 'Deactivate address' : 'Activate address'}
            >
              {togglingId === address.id ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : address.is_active ? (
                <Eye className="w-3.5 h-3.5" />
              ) : (
                <EyeOff className="w-3.5 h-3.5" />
              )}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
              onClick={(e) => {
                e.stopPropagation();
                handleStartEdit(address);
              }}
              title="Edit address"
            >
              <Pencil className="w-3 h-3" />
            </Button>
            {confirmDeleteId === address.id ? (
              <div className="flex items-center gap-1 ml-1">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 text-[10px]"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(address.id!);
                  }}
                >
                  Confirm
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmDeleteId(null);
                  }}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0 text-muted-foreground hover:text-red-400"
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmDeleteId(address.id!);
                }}
                title="Delete address"
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Loading addresses...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Home Address Section - always visible regardless of open approval */}
      <Card className="bg-secondary/30 border-border">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
              <Home className="w-4 h-4 text-blue-400" />
              Home Address
            </CardTitle>
            {!readOnly && !homeAddress && !isAdding && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="border-blue-600/60 text-blue-400 hover:bg-blue-900/30 h-7 text-xs"
                onClick={() => handleStartAdd('home')}
              >
                <Plus className="w-3 h-3 mr-1" />
                Set Home Address
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isAdding && addType === 'home' ? (
            renderAddressForm()
          ) : homeAddress ? (
            renderHomeCard(homeAddress)
          ) : (
            <p className="text-sm text-muted-foreground text-center py-3">
              No home address set. {!readOnly && 'Click "Set Home Address" to add one.'}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Approved Destinations Section - hidden when open approval is on */}
      {!openApproval && (
        <Card className="bg-secondary/30 border-border">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Approved Destinations
                <Badge variant="secondary" className="text-xs">{destinations.length}</Badge>
                {savingOrder && (
                  <span className="flex items-center gap-1 text-[10px] text-yellow-400 font-normal normal-case">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Saving order...
                  </span>
                )}
              </CardTitle>
              {!readOnly && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-border text-muted-foreground hover:text-foreground h-7 text-xs"
                  onClick={() => handleStartAdd('destination')}
                  disabled={isAdding}
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Add Destination
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {isAdding && addType === 'destination' && (
              <div className="mb-3">
                {renderAddressForm()}
              </div>
            )}
            {destinations.length === 0 && !isAdding ? (
              <p className="text-sm text-muted-foreground text-center py-3">
                No approved destinations added yet.{' '}
                {!readOnly && 'Click "+ Add Destination" to add approved travel addresses.'}
              </p>
            ) : (
              <>
                {!readOnly && destinations.length > 1 && !editingId && !isAdding && (
                  <p className="text-[10px] text-muted-foreground mb-2 flex items-center gap-1.5">
                    <GripVertical className="w-3 h-3" />
                    Drag addresses to reorder. Changes are saved automatically.
                  </p>
                )}
                <div ref={destListRef} className="space-y-2">
                  {destinations.map((dest, index) => (
                    <div key={dest.id}>
                      {renderDestinationCard(dest, index)}
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CRMBookableAddresses;
